import { html, css, cssManager, customElement, state, DeesElement, directives } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { demoPage } from '../../../demos/templates.js';
import type { DeesSimpleLogin, IDeesLoginProvider, IDeesLoginProviderContext, TDeesLoginPasskeyIntent, TDeesLoginMethod } from './dees-simple-login.js';
import '../../00group-input/dees-input-dropdown/dees-input-dropdown.js';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';

const providers: IDeesLoginProvider[] = [
  { id: 'workspace', label: 'Workspace account', icon: 'lucide:building2' },
  { id: 'idp-global', label: 'idp.global', icon: 'lucide:shieldCheck' },
];
const variants = [
  { key: 'full', option: 'All sign-in methods' }, { key: 'password', option: 'Password only' },
  { key: 'passkey', option: 'Passkey only' }, { key: 'enroll', option: 'Passkey enrollment' },
  { key: 'providers', option: 'Identity providers' }, { key: 'many', option: 'Six providers' },
  { key: 'events', option: 'Consumer-managed events' }, { key: 'unavailable', option: 'Unavailable passkey' },
];

@customElement('dees-demo-simple-login')
class DeesDemoSimpleLogin extends DeesElement {
  @state() private accessor variant = 'full';
  @state() private accessor outcome = 'success';
  @state() private accessor revision = 0;
  @state() private accessor result = 'Choose a sign-in method. These accounts and responses are local samples.';
  private work = new AbortController();
  private get login(): DeesSimpleLogin | null { return this.shadowRoot?.querySelector('dees-simple-login') || null; }

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: grid; gap: 16px; font-family: var(--dees-font-family); }
    .controls { display: flex; align-items: end; flex-wrap: wrap; gap: 16px; }
    .controls dees-input-dropdown { width: 250px; max-width: 100%; }
    .controls dees-button { margin-left: auto; }
    .stage { height: 720px; min-height: 0; position: relative; border: 1px solid var(--dees-color-border-subtle); border-radius: var(--dees-radius-2xl); corner-shape: var(--dees-corner-shape); overflow: hidden; }
    .authenticated { display: grid; place-content: center; justify-items: center; text-align: center; gap: 16px; height: 100%; padding: 32px; background: var(--dees-color-bg-canvas); }
    .authenticated > dees-icon { color: var(--dees-color-text-success); }
    h2 { margin: 0; font-size: 28px; font-weight: 600; letter-spacing: -.03em; }
    p, output { margin: 0; color: var(--dees-color-text-secondary); font-size: 13px; line-height: 1.5; }
    output { min-height: 40px; padding: 12px 16px; background: var(--dees-color-bg-secondary); border-radius: var(--dees-radius-lg); }
    @media (max-width: 480px) { .stage { height: 760px; } }
  `];

  private resetDemo(): void {
    this.work.abort(); this.work = new AbortController(); this.revision++;
    this.result = 'Ready. No account or personal information is required.';
  }
  public async disconnectedCallback(): Promise<void> { this.work.abort(); await super.disconnectedCallback(); }
  public async connectedCallback(): Promise<void> { if (this.work.signal.aborted) this.work = new AbortController(); await super.connectedCallback(); }

  private async respond(method: string, eventTarget?: { method: TDeesLoginMethod; providerId?: string; intent?: TDeesLoginPasskeyIntent }): Promise<void> {
    const login = this.login;
    if (!login) return;
    const signal = this.work.signal;
    const outcome = this.outcome;
    if (eventTarget) login.setBusy(eventTarget, true);
    const form = eventTarget?.method === 'password' ? login.shadowRoot?.querySelector('dees-form') : null;
    form?.setStatus('pending', 'Signing in…');
    this.result = `Waiting for ${method}…`;
    const completed = await new Promise<boolean>(resolve => {
      const abort = () => { window.clearTimeout(timer); resolve(false); };
      const timer = window.setTimeout(() => { signal.removeEventListener('abort', abort); resolve(true); }, 700);
      signal.addEventListener('abort', abort, { once: true });
    });
    if (!completed || signal.aborted || !login.isConnected) return;
    if (eventTarget) login.setBusy(eventTarget, false);
    form?.setStatus('normal', 'Sign in');
    if (outcome === 'error') {
      this.result = `${method} was declined. The other sign-in methods remain available.`;
      if (eventTarget) login.reportError(eventTarget, 'Could not sign in. Try again or choose another method.');
      else throw new Error('Could not sign in. Try again or choose another method.');
      return;
    }
    this.result = `Signed in with ${method}.`;
    await login.switchToSlottedContent();
    if (signal.aborted || !login.isConnected) return;
    await this.updateComplete;
    login.querySelector('dees-button')?.focus();
  }

  private signOut(): void {
    const login = this.login;
    if (!login) return;
    login.reset();
    const root = login.shadowRoot!;
    root.querySelector('dees-form')?.setStatus('normal', 'Sign in');
    Object.assign(root.querySelector<HTMLElement>('.login')!.style, { opacity: '1', transform: 'translateY(0px)' });
    root.querySelector<HTMLElement>('.loginContainer')!.style.pointerEvents = 'all';
    Object.assign(root.querySelector<HTMLElement>('.slotContainer')!.style, { opacity: '0', pointerEvents: 'none' });
    this.result = 'Signed out. The same login surface is ready again.';
    requestAnimationFrame(() => {
      if (!login.isConnected) return;
      const button = root.querySelector('dees-button');
      const input = root.querySelector('dees-input-text');
      (button || input)?.focus();
    });
  }

  public render() {
    const v = this.variant;
    const eventMode = v === 'events';
    const passkey = ['full', 'passkey', 'enroll', 'events', 'unavailable'].includes(v);
    const password = ['full', 'password', 'many', 'events'].includes(v);
    const configuredProviders = ['full', 'providers', 'events'].includes(v) ? providers : v === 'many' ? [
      ...providers, { id: 'email', label: 'Email link', icon: 'lucide:mail' }, { id: 'key', label: 'Security key', icon: 'lucide:keyRound' },
      { id: 'card', label: 'Smartcard', icon: 'lucide:contact' }, { id: 'directory', label: 'Directory', icon: 'lucide:network', disabled: true },
    ] : [];
    return html`<div class="controls">
      <dees-input-dropdown label="Configuration" .options=${variants} .selectedOption=${variants.find(variant => variant.key === v)!}
        @selectedOption=${(event: CustomEvent) => { this.variant = event.detail.key; this.resetDemo(); }}></dees-input-dropdown>
      <dees-input-multitoggle label="Demo response" .options=${[{ key: 'success', label: 'Success' }, { key: 'error', label: 'Error' }]} .value=${this.outcome}
        @change=${(event: CustomEvent) => { this.outcome = event.detail.value; }}></dees-input-multitoggle>
      <dees-button type="ghost" text="Reset demo" @clicked=${this.resetDemo}></dees-button>
    </div>
    <div class="stage">${directives.keyed(`${v}-${this.revision}`, html`<dees-simple-login name="Studio" .passkey=${passkey} .passkeyAvailable=${v !== 'unavailable'} .password=${password}
      .providers=${configuredProviders} .passkeyIntents=${v === 'enroll' ? ['authenticate', 'register'] : ['authenticate']}
      .labels=${{ heading: v === 'enroll' ? 'Your key to Studio' : 'Welcome to Studio', subheading: v === 'enroll' ? 'Sign in or create a passkey for this device.' : 'A little space for your next big idea.' }}
      .passkeyLoginHandler=${eventMode ? undefined : () => this.respond('your passkey')}
      .passkeyRegisterHandler=${() => this.respond('a new passkey')}
      .providerLoginHandler=${eventMode ? undefined : (context: IDeesLoginProviderContext) => this.respond(context.provider.label)}
      .passwordLoginHandler=${eventMode || v === 'password' ? undefined : () => this.respond('your password')}
      @passkey-login=${() => { if (eventMode) void this.respond('your passkey', { method: 'passkey' }); }}
      @provider-login=${(event: CustomEvent) => { if (eventMode) void this.respond(event.detail.provider.label, { method: 'provider', providerId: event.detail.providerId }); }}
      @login=${() => { if (eventMode || v === 'password') void this.respond('your password', { method: 'password' }); }}>
      <dees-icon slot="brand" icon="lucide:layers" iconSize="26"></dees-icon>
      <div class="authenticated"><dees-icon icon="lucide:circleCheck" iconSize="36"></dees-icon><h2>You're in.</h2><p>Your Studio workspace is ready.</p>
        <dees-button type="accent" text="Sign out" @clicked=${this.signOut}></dees-button>
      </div>
    </dees-simple-login>`)}</div><output role="status">${this.result}</output>`;
  }
}

export const demoFunc = () => demoPage('Sign in', 'One welcoming surface. Passkeys, workspace accounts, and passwords working together.', html`<dees-demo-simple-login></dees-demo-simple-login>`);
